import { useRef, useContext } from "react";
import { Message, Mode } from "../types/tutor.types";
import { useTutorChatStore } from "../store/useTutorChatStore";
import { useUserQuery } from "./queries/useUserQuery";
import { useGroupListStore } from "../store/useGroupListStore";
import { tutorApiService } from "../services/tutorApi";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useUIStore } from "../store";
import {
  useUpdateGroupMutation,
  useCreateGroupMutation,
} from "./queries/mutations/useGroupMutations";
import { MyContext } from "../../../context/MyContext";
import getUserState from "../utils/getUserState";
import { useNavigate } from "react-router-dom";
import { useDeviceGuard } from "../../../hooks/useDeviceGuard";
import {
  getConversationKey,
  promoteConversationKey,
} from "../../../utils/contentDeviceCache";

const useChat = (mode: Mode) => {
  const { showLoginModal } = useAuth();
  const { isLoggedIn, user, refetchUser } = useUserQuery();
  // currentGroupId는 전송 시점에 getState()로 직접 읽는다 (아래 activeGroupId 참고)
  const { setCurrentGroupId } = useGroupListStore();
  const { setIsOpenTicketAlert } = useContext(MyContext);
  const { ensureDeviceRegistered, isCheckingDevice } = useDeviceGuard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setIsFreeScroll, pageName } = useUIStore();
  const {
    addMessage,
    removeMessage,
    startStreamingMessage,
    appendStreamChunk,
    completeStreamingMessage,
    abortStreamingMessage,
    setStreamingMessageId,
    setAbortController,
    cancelStreaming,
    setSearchContent,
    setFetching,
  } = useTutorChatStore();

  // Mutations
  const updateGroupMutation = useUpdateGroupMutation(mode);
  const createGroupMutation = useCreateGroupMutation(mode, pageName, true); // skipNavigation: true

  // Refs (로컬에서만 필요한 것들)
  const streamStartTime = useRef<number>(0);
  const isCreatingGroupRef = useRef<boolean>(false); // 그룹 생성 중복 방지

  // 최소 로딩 시간 (ms) - 빠른 응답에서도 로딩 상태가 보이도록
  const MINIMUM_LOADING_TIME = 300;

  const submitMessage = async (
    value: string,
    options?: {
      callback?: () => void;
      isRegenerate?: boolean;
      oldResponseId?: string;
    }
  ): Promise<void> => {
    const callback = options?.callback;
    // 1. 유효성 검사
    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    if (!isLoggedIn) {
      showLoginModal();
      return;
    }

    // 1.5. 티켓 유효성 검사
    // 세 가지 조건 확인:
    // (1) 이용권 소진 시
    // (2) 무료 횟수 소진 시 (paid가 false이고 카운트가 0)
    // (3) 이용권 기간 종료 시 (end_dt 체크)

    const ticketCount = mode === Mode.TUTOR ? user?.tutor_cnt : user?.socra_cnt;
    const userState = getUserState(user);

    // 조건 (1): 이용권 소진 - 카운트가 0 이하
    const isTicketExhausted = !ticketCount || ticketCount <= 0;

    // 조건 (2): 무료 횟수 소진 - 미구독 상태(paid: false)에서 카운트가 0
    const isFreeTrialExhausted = !user?.paid && isTicketExhausted;

    // 조건 (3): 이용권 기간 종료 - userState가 'expired'
    const isSubscriptionExpired = userState === "expired";

    // 세 가지 조건 중 하나라도 해당되면 모달 표시
    if (isTicketExhausted || isFreeTrialExhausted || isSubscriptionExpired) {
      setIsOpenTicketAlert(true);
      return;
    }

    // 2. 그룹 ID 확보 (없으면 임시 ID로 시작 후 실제 생성)
    // 초과 안내 → 기기 해제 → 재시도 경로에서는 이 함수가 예전 렌더의 클로저로
    // 다시 실행되므로, 그룹만은 호출 시점의 최신 값을 직접 읽는다.
    // (그러지 않으면 그 사이 다른 대화방으로 이동했을 때 예전 그룹에 저장된다)
    const activeGroupId = useGroupListStore.getState().currentGroupId;

    // 1.6. 기기 등록 판정 (업무 #11104)
    // 슬롯 초과/인증 만료 시 전송을 중단하며, 입력 메시지는 그대로 유지된다.
    // 기기 해제 후 "다시 시도"하면 아래 전송이 그대로 이어서 실행된다.
    // 판정은 대화 단위로 1회만 수행한다 (뷰어의 "뷰어 오픈 시 1회"와 동일 원칙).
    const isDeviceRegistered = await ensureDeviceRegistered({
      conversationKey: getConversationKey(mode, activeGroupId),
      onRetry: () => submitMessage(value, options),
    });
    if (!isDeviceRegistered) return;

    let groupId = activeGroupId;
    let isNewGroup = false;
    const timestamp = Date.now();

    // 그룹이 없으면 임시 ID 할당 (UI 즉시 표시용)
    if (!groupId) {
      // 중복 생성 방지: 이미 그룹 생성 중이면 대기
      if (isCreatingGroupRef.current) {
        console.log(
          "그룹 생성이 이미 진행 중입니다. 잠시 후 다시 시도해주세요."
        );
        return;
      }

      isNewGroup = true;
      groupId = `temp-${timestamp}`; // 임시 그룹 ID로 UI 먼저 표시
    }

    // Free Scroll 비활성화 (메시지 전송 시 자동 스크롤 활성화)
    setIsFreeScroll(false);

    // 3. 사용자 메시지 생성 및 추가 (임시 또는 실제 그룹 ID)
    const userMessage: Message = {
      id: `user-${timestamp}`,
      group_id: groupId,
      content: trimmedValue,
      sender: "user",
      created_at: new Date().toISOString(),
    };

    addMessage(userMessage);

    // 3.5. 검색어 설정 및 로딩 상태 설정 (로딩 메시지 표시를 위해)
    setSearchContent(trimmedValue);
    setFetching(true);

    // 4. AI 메시지 시작 (빈 content, is_loading: true)
    const aiMessageId = `ai-${timestamp}`;
    setStreamingMessageId(aiMessageId); // 전역 상태로 저장
    streamStartTime.current = Date.now(); // 스트리밍 시작 시간 기록
    startStreamingMessage(aiMessageId, groupId);

    // 5. 새 그룹인 경우 실제 그룹 생성 (백그라운드에서 진행)
    if (isNewGroup) {
      isCreatingGroupRef.current = true; // 생성 시작 플래그 설정

      try {
        // Tutor 모드: 사용자 메시지를 제목으로 사용
        // Socrates 모드: 기본 제목 사용 (나중에 AI 응답으로 업데이트)
        const newGroup = await createGroupMutation.mutateAsync({
          title: mode === Mode.TUTOR ? trimmedValue : "새 대화",
          skipClearHistory: true, // 스트리밍 메시지 보존
        });

        const realGroupId = newGroup.id;

        // 임시 그룹 ID를 실제 ID로 업데이트 (batch 업데이트로 상태 안정성 보장)
        useTutorChatStore.setState((state) => ({
          history: state.history.map((msg) =>
            msg.group_id === groupId ? { ...msg, group_id: realGroupId } : msg
          ),
        }));

        // 실제 그룹 ID로 변경 (이후 로직에서 사용)
        groupId = realGroupId;

        // 수동으로 currentGroupId 설정 및 네비게이션 (메시지 ID 업데이트 후)
        setCurrentGroupId(realGroupId);

        // 판정 캐시의 "새 대화" 키를 실제 대화 ID로 승격한다.
        // 승격하지 않으면 다음 메시지에서 대화가 바뀐 것으로 오인해 재판정한다.
        promoteConversationKey(getConversationKey(mode, realGroupId));
        navigate(
          `/${mode === Mode.TUTOR ? "tutor" : "socrates"}/${realGroupId}`
        );
      } catch (error) {
        console.error("그룹 생성 실패:", error);
        // 그룹 생성 실패 시 메시지 제거
        removeMessage(userMessage.id);
        removeMessage(aiMessageId);
        setSearchContent("");
        setFetching(false);
        return;
      } finally {
        isCreatingGroupRef.current = false; // 생성 완료/실패 플래그 해제
      }
    }

    // 5. 입력창 초기화
    callback && callback();

    // 6. 채팅 히스토리 조회 (CLOVA-X 요청용)
    try {
      // 현재 그룹의 채팅 히스토리 조회
      const chatHistory = queryClient.getQueryData<any>([
        "chat-history",
        mode,
        groupId,
      ]);

      // Tutor 모드: 첫 메시지 전송 전 그룹명 자동 업데이트
      // (단, 그룹을 방금 생성한 경우는 이미 제목이 설정되었으므로 제외)
      if (
        mode === Mode.TUTOR &&
        (!chatHistory || chatHistory.length === 0) &&
        activeGroupId
      ) {
        updateGroupMutation.mutate({
          groupId: groupId,
          title: trimmedValue,
        });
      }

      // 레거시 형식으로 변환: [{role: "user", content: "..."}, {role: "assistant", content: "..."}]
      const messages = chatHistory
        ? chatHistory.flatMap((item: any) => [
            { role: "user" as const, content: item.input_text },
            { role: "assistant" as const, content: item.response },
          ])
        : [];

      // 6. 스트리밍 시작
      // AbortController 생성 및 전역 상태로 저장
      const controller = new AbortController();
      setAbortController(controller);

      // 완료 데이터 저장용 ref
      const completeDataRef = { current: null as any };

      const result = await tutorApiService.sendMessageStreaming(
        {
          query: trimmedValue,
          messages: messages,
          persona: user?.tone || "선생님 말투",
          is_recommended: false,
        },
        // onChunk - 청크 수신 콜백
        (chunk: string) => {
          // aiMessageId는 클로저로 캡처됨
          appendStreamChunk(aiMessageId, chunk);
        },
        // onMessageId - 서버에서 메시지 ID 수신 (로깅용)
        (messageId: string) => {},
        // onError - 에러 처리
        (error: Error) => {
          abortStreamingMessage(
            aiMessageId,
            "응답 생성 중 문제가 발생했습니다."
          );
          cancelStreaming(); // 상태 정리
        },
        // onComplete - 스트리밍 완료 콜백
        (data: any) => {
          completeDataRef.current = data;
        }
      );

      // 6. 스트리밍 결과 처리
      if (result === "success") {
        // 최소 로딩 시간 보장 (빠른 응답에서도 로딩 상태가 보이도록)
        const elapsed = Date.now() - streamStartTime.current;
        if (elapsed < MINIMUM_LOADING_TIME) {
          await new Promise((resolve) =>
            setTimeout(resolve, MINIMUM_LOADING_TIME - elapsed)
          );
        }

        completeStreamingMessage(aiMessageId);

        // searchContent 초기화
        setSearchContent("");

        // 7. 백엔드에 채팅 저장 (레거시 deliverResult 로직)
        if (completeDataRef.current) {
          try {
            const chatUrl =
              process.env.REACT_APP_BACK_TUTOR_HISTORY || "/api/v1/tutor/his";
            const chatData = {
              ...completeDataRef.current, // CLOVA-X 전체 응답 사용 (레거시 방식)
              input_text: trimmedValue, // 사용자 입력만 덮어쓰기
              // Regenerate 모드: 기존 response_id 유지
              ...(options?.isRegenerate &&
                options?.oldResponseId && {
                  response_id: options.oldResponseId,
                }),
            };

            // touchAPIWithGroupFollow: 채팅 저장 + 그룹 연결
            // Regenerate 모드: PATCH, 일반 모드: POST
            await tutorApiService.touchAPIWithGroupFollow(
              options?.isRegenerate ? "PATCH" : "POST",
              chatUrl,
              chatData,
              parseInt(groupId)
            );

            // 캐시 무효화
            queryClient.invalidateQueries({
              queryKey: ["chat-history", mode],
            });

            // 명시적으로 최신 데이터 fetch (완료될 때까지 대기)
            await queryClient.fetchQuery({
              queryKey: ["chat-history", mode, groupId],
              queryFn: async () => {
                return await tutorApiService.fetchChatHistory(mode, groupId);
              },
            });

            // 데이터 fetch 완료 후 임시 메시지 제거
            removeMessage(userMessage.id);
            removeMessage(aiMessageId);

            // 이전 에러 메시지가 있으면 제거 (오프라인 상황에서 "error: error" 메시지 정리)
            // ID 패턴: user-${timestamp}, ai-${timestamp} → 같은 timestamp 찾아 쌍 제거
            const { history: currentHistory } = useTutorChatStore.getState();
            const errorMessages = currentHistory.filter(
              (msg) => msg.group_id === groupId && msg.error
            );

            // 에러 메시지와 같은 timestamp의 사용자 메시지 제거
            errorMessages.forEach((errorMsg) => {
              removeMessage(errorMsg.id);

              // ID 패턴으로 짝인 사용자 메시지 찾기: ai-${timestamp} → user-${timestamp}
              const timestamp = errorMsg.id.replace("ai-", "");
              const userMessageId = `user-${timestamp}`;

              // 같은 그룹에서 매칭되는 사용자 메시지가 있으면 제거
              const userMsg = currentHistory.find(
                (msg) => msg.id === userMessageId && msg.group_id === groupId
              );
              if (userMsg) {
                removeMessage(userMessageId);
              }
            });

            // Socrates 모드: 첫 응답 후 그룹명 자동 업데이트 (20자 제한)
            if (mode === Mode.SOCRATES) {
              // 캐시 무효화 후 최신 히스토리 조회
              const updatedChatHistory = queryClient.getQueryData<any>([
                "chat-history",
                mode,
                groupId,
              ]);

              // 첫 응답인 경우 (히스토리가 1개)
              if (updatedChatHistory && updatedChatHistory.length === 1) {
                const aiResponse = completeDataRef.current.response || "";
                const truncatedTitle =
                  aiResponse.length > 20
                    ? aiResponse.substring(0, 20)
                    : aiResponse;

                updateGroupMutation.mutate({
                  groupId: groupId,
                  title: truncatedTitle,
                });
              }
            }

            // 메시지 전송 완료 후 사용자 정보 refetch (이용권 카운트 갱신)
            refetchUser();
          } catch (saveError) {
            console.warn("백엔드 저장 실패 (스트리밍은 정상 완료):", saveError);
            // 저장 실패해도 스트리밍 메시지는 UI에 유지됨
            // TODO: 레거시 touchAPI 방식으로 변경하거나 백엔드 API 수정 필요
          }
        }
      } else if (result === "aborted") {
        // 사용자가 중단한 경우
        abortStreamingMessage(
          aiMessageId,
          "사용자가 응답 생성을 중단했습니다."
        );
        setSearchContent("");
      }
    } catch (error) {
      console.error("handleSubmit 에러:", error);
      abortStreamingMessage(aiMessageId, "응답 생성 중 문제가 발생했습니다.");
      setSearchContent("");
    } finally {
      cancelStreaming(); // 전역 상태 정리
      setFetching(false);
    }
  };

  return {
    submitMessage,
    isCheckingDevice,
  };
};

export default useChat;
